import React, {Component} from 'react'
import propTypes from 'prop-types'
import {connect} from 'react-redux'
import moment from 'moment'
import {get, isEmpty, isEqual, isNil} from 'lodash'
import themeVars from 'theme/themeVars'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import TextField from '@material-ui/core/TextField'
import {SelectField} from 'components/selectField'
import CompanySelectField from '../../components/companySelectField'
import ContactSelectField from '../../components/contactSelectField'
import OpportunitySelectField from '../../components/opportunitySelectField'
import ReferenceSelectField from '../../components/referenceSelectField'
import OrderSelectField from '../../components/orderSelectField'
import QuoteFinder from '../../containers/quoteFinder'
import DatePicker from '../../components/datePicker'
import TimePicker from '../../components/timePicker'
import Icon from '../../components/icon'
import CampaignSelector from '../../containers/campaignSelector'
import DateHelper, {setTimeToMidnight} from '../../helpers/dates'
import NumberComponentField from '../../components/numberField'
import {prefix} from 'helpers/component'
import {formatRoundedNumber, round} from 'helpers/financial'
import PartCategorySelectField from 'components/partCategorySelectField'
import PartSelectField from 'components/partSelectField'
import {PeriodUnit, periodUnitsDaysToYears, periodUnitsMinsToMonths} from 'helpers/activities'
import Phone from 'components/phone'
import Email from 'components/email'
import DeliveryNoteSelectField from 'components/deliveryNoteSelectField'
import PurchaseOrderSelectField from 'components/purchaseOrderSelectField'
import DateComponent from 'containers/date'
import {selectAllText} from 'helpers/ui'
import {QuoteStatus} from 'helpers/quotes'
import DatePickerRangeSelectField from 'components/datePickerRangeSelectField'
import {t} from "helpers/language"
import HintIcon from 'components/hintIcon'
import {quotingContextSelectorOrdersPivotTableKey} from "reducers/quotingContext/selector"
import {
  FieldOptionType,
  getRequiredFieldProps,
  isFieldOptionButtonType,
  isFieldRequired,
  showDateTimeFieldDateValue,
  showDateTimeFieldTimeValue,
} from 'helpers/fields'
import './formField.scss'

const getClassName = prefix('formField-')

const getSelectFieldProps = () => {
  return {
    flat: true,
    styles: {
      control: provided => ({
        ...provided,
        border: 'none',
        boxShadow: 'none',
      }),
      valueContainer: provided => ({
        ...provided,
        paddingLeft: 0,
        paddingRight: 0,
      }),
      singleValue: provided => ({
        ...provided,
        margin: 0,
      }),
      input: provided => ({
        ...provided,
        margin: 0,
      }),
      placeholder: provided => ({
        ...provided,
        fontSize: 16,
        color: themeVars.palette.disabledColor,
      }),
    },
  }
}

class FieldDivider extends Component {
  getStyles = () => {
    const {active, borderBottomWidth} = this.props

    return {
      container: {
        position: 'relative',
        width: '100%',
      },
      line: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        borderBottom: `${borderBottomWidth}px solid ${themeVars.palette.borderColor}`,
      },
      active: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        borderBottom: '2px solid',
        borderColor: themeVars.palette.primary1Color,
        transform: `scaleX(${active ? 1 : 0})`,
        transition: 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
      },
    }
  }

  render() {
    const styles = this.getStyles()
    const {active, container, line} = styles

    return (
      <div style={container} className={getClassName('divider')}>
        <div className={'line'} style={line}/>
        <div style={active}/>
      </div>
    )
  }
}

FieldDivider.defaultProps = {
  active: false,
  borderBottomWidth: 1,
}

FieldDivider.propTypes = {
  active: propTypes.bool.isRequired,
  borderBottomWidth: propTypes.number,
}

class FormField extends Component {
  componentDidMount() {
    const {autoFocus, onLabelClick} = this.props
    if (autoFocus && onLabelClick) {
      onLabelClick()
      selectAllText()
    }
  }

  getStyles = () => {
    const {
      active,
      disabled,
      width,
      showLabel,
      labelWidth = 150,
      labelMaxWidth,
      validateState,
      mandatory,
      value,
    } = this.props
    const isValidateStateRequired = isFieldRequired(validateState, mandatory, value)

    return {
      container: {
        width,
      },
      content: {
        display: 'flex',
        alignItems: 'center',
        minHeight: 48,
        backgroundColor: disabled ? themeVars.palette.disabledBackgroundColor : null,
        position: 'relative',
        paddingLeft: showLabel ? labelWidth + 24 : 0,
      },
      label: {
        position: 'absolute',
        display: 'flex',
        top: 11,
        left: 24,
        flexGrow: 0,
        flexShrink: 0,
        color: disabled
          ? themeVars.palette.primaryFontColor
          : active || isValidateStateRequired
            ? themeVars.palette.primary1Color
            : themeVars.palette.disabledColor,
        backgroundColor: disabled ? themeVars.palette.disabledBackgroundColor : null,
        fontSize: 16,
        overflow: 'hidden',
        maxWidth: !isNil(labelMaxWidth) && labelMaxWidth,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      hint: {
        fontSize: 12,
        color: themeVars.palette.primary1Color,
        marginLeft: labelWidth + 24,
      },
      entry: {
        height: '100%',
        flexGrow: 1,
      },
    }
  }

  getLabel = () => {
    const {showToggle, label, toggled, onToggleChange} = this.props
    return showToggle
      ? <FormControlLabel
        classes={{root: 'formField-toggleControl-root'}}
        control={<Switch
          classes={{root: 'formField-toggleSwitch-root'}}
          checked={toggled}
          onChange={onToggleChange}
        />}
        label={label}
      />
      : label
  }

  getLabelElement = (showLabel, styles, readOnly, onLabelClick) => {
    return showLabel
      ? (
        <span
          style={styles.label}
          className={getClassName('label')}
          onClick={readOnly
            ? () => {
            }
            : onLabelClick}>
          {this.getHintIcon()}
          {this.getLabel()}
            </span>
      )
      : null
  }

  getHint = (hint, styles) => {
    return hint
      ? <div style={styles.hint} className={getClassName('hint')}>{hint}</div>
      : null
  }

  getHintIcon = () => {
    const {hintIcon, onHintIconClick} = this.props
    return isEmpty(hintIcon)
      ? null
      : <HintIcon className={getClassName('hint-icon')} light
                  hintContent={<div className={getClassName('hintBox-content')}>
                    <div className={getClassName('hintBox-body')}>{hintIcon}</div>
                  </div>} hintBoxHasPointer onClick={onHintIconClick}/>
  }

  getDivider = (showDivider, active, borderBottomWidth) => {
    return showDivider
      ? <FieldDivider
        active={active}
        borderBottomWidth={borderBottomWidth}/>
      : null
  }

  render() {
    const {
      className,
      hint,
      active,
      onLabelClick,
      showLabel,
      readOnly,
      showDivider,
      borderBottomWidth,
    } = this.props
    const styles = this.getStyles()
    const {container, content, entry} = styles

    return (
      <div className={`${getClassName('container')} ${className}`} style={container}>
        <div style={content}>
          {this.getLabelElement(showLabel, styles, readOnly, onLabelClick)}
          <span style={entry}>{this.props.children}</span>
        </div>
        {this.getHint(hint, styles)}
        {this.getDivider(showDivider, active, borderBottomWidth)}
      </div>
    )
  }
}

FormField.propTypes = {
  className: propTypes.string,
  fieldKey: propTypes.string,
  borderBottomWidth: propTypes.number,
  label: propTypes.any,
  labelWidth: propTypes.number,
  labelMaxWidth: propTypes.number,
  hint: propTypes.string,
  hintIcon: propTypes.string,
  onHintIconClick: propTypes.func,
  active: propTypes.bool,
  disabled: propTypes.bool,
  readOnly: propTypes.bool,
  showDivider: propTypes.bool,
  onLabelClick: propTypes.func,
  showLabel: propTypes.bool,
  width: propTypes.oneOfType([propTypes.string, propTypes.number]),
  children: propTypes.any,
  showToggle: propTypes.bool,
  toggled: propTypes.bool,
  autoFocus: propTypes.bool,
  onToggleChange: propTypes.func,
  mandatory: propTypes.bool,
  validateState: propTypes.bool,
  value: propTypes.any,
}

FormField.defaultProps = {
  className: '',
  showLabel: true,
  width: '100%',
  showToggle: false,
  toggled: false,
  showDivider: true,
  mandatory: false,
  validateState: false,
}

class TextFormField extends Component {
  constructor(props) {
    super(props)
    this.state = {
      value: this.props.value,
      focused: false,
    }
  }

  componentDidUpdate(prevProps) {
    const {value} = this.props
    if (prevProps.value !== value) {
      this.setState({value})
    }
  }

  componentWillUnmount() {
    if (this.focusTimer) clearTimeout(this.focusTimer)
  }

  focus = () => {
    if (this.input) this.input.focus()
  }

  onFocus = (e) => {
    const {onFocus, autoFocusDelay} = this.props

    this.focusTimer = setTimeout(() => {
      this.focusTimer = null
      this.setState({focused: true})

      if (onFocus)
        onFocus(e)
    }, autoFocusDelay)
  }

  onBlur = () => {
    const {onMask, onChange, onBlur} = this.props
    let {value} = this.state

    if (onMask) value = onMask(value)
    this.setState({focused: false, value})

    if (value !== this.props.value)
      onChange(value)

    if (onBlur)
      onBlur()
  }

  onChange = (event) => {
    const value = event.target.value
    const {immediate, onChange} = this.props
    this.setState({value})

    if (immediate)
      onChange(value)
  }

  onClick = (e) => {
    const {onClick} = this.props
    if (onClick)
      onClick(e)
  }

  onLabelClick = () => {
    this.input.focus()
  }

  getStyles = () => {
    return {
      textField: {
        height: 'auto',
      },
      hint: {
        top: '50%',
        marginTop: -12,
        bottom: 'auto',
        marginBottom: 12,
      },
      content: {
        display: 'flex',
        alignItems: 'center',
      },
      prepend: {
        paddingLeft: 5,
        paddingRight: 5,
      },
      append: {
        paddingLeft: 5,
        paddingRight: 5,
      },
    }
  }

  getReadOnlyLabelField = (fieldProps) => {
    const {
      append,
      label,
      labelWidth,
      options,
      optionType,
      placeholder,
      prepend,
      styles,
      value,
    } = fieldProps

    const labelField = (
      <LabelField
        label={label}
        labelWidth={labelWidth}
        placeholder={placeholder}
        value={`${prepend}${value}${append}`}
      />
    )

    return optionType === FieldOptionType.BUTTON
      ? (
        <div className={'label-field-button-wrapper'}>
          {labelField}
          <div style={styles.content}>
            {options.append}
          </div>
        </div>
      )
      : labelField
  }

  render() {
    const {
      autoFocus,
      disabled,
      errorText,
      fieldKey,
      formFieldClassName,
      hint,
      hintIcon,
      label,
      labelWidth,
      labelMaxWidth,
      mandatory,
      multiLine,
      onHintIconClick,
      options,
      password,
      placeholder,
      readOnly,
      rows,
      rowsMax,
      showDivider,
      showLabel,
      validateState,
    } = this.props

    const styles = this.getStyles()
    const {focused} = this.state
    let {value} = this.state

    if (isNil(value))
      value = ''

    if (readOnly) {
      const prepend = options && options.prepend ? options.prepend : ''
      const optionType = get(options, 'type', FieldOptionType.FIELD)
      let append = options && options.append ? options.append : ''
      if (isFieldOptionButtonType(optionType))
        append = ''

      const fieldProps = {
        styles,
        options,
        optionType,
        label,
        prepend,
        value,
        append,
        placeholder,
        labelWidth,
      }

      return this.getReadOnlyLabelField(fieldProps)
    }

    const type = (password) ? 'password' : 'text'
    const maxLength = options && options.length || null
    const reqProps = getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)
    // v0 hintText accepted React nodes (e.g. translation wrappers from t()), but v1
    // placeholder is a plain HTML attribute and only accepts strings. Fall back to a
    // literal when the hint is a React element.
    const placeholderText = typeof reqProps.hintText === 'string'
      ? reqProps.hintText
      : (mandatory ? 'Required' : '')

    const content = (
      <TextField
        autoFocus={autoFocus}
        className={reqProps.className}
        disabled={disabled}
        error={!!reqProps.errorText}
        fullWidth={true}
        helperText={reqProps.errorText || undefined}
        id={'standard-helperText'}
        InputProps={{
          disableUnderline: true,
          readOnly,
        }}
        inputProps={{
          maxLength,
        }}
        multiline={multiLine}
        name={fieldKey}
        onBlur={this.onBlur}
        onChange={this.onChange}
        onClick={this.onClick}
        onFocus={this.onFocus}
        inputRef={e => this.input = e}
        required={mandatory}
        rows={rows}
        rowsMax={multiLine && rows && !rowsMax ? 9999 : rowsMax}
        placeholder={placeholderText}
        style={styles.textField}
        type={type}
        value={value}
      />
    )

    const prependEl = options && options.prepend
      ? <div key="prepend" style={styles.prepend}>{options.prepend}</div>
      : null
    const appendEl = options && options.append
      ? <div key="append" style={styles.append}>{options.append}</div>
      : null

    return (
      <FormField
        active={focused}
        className={formFieldClassName || fieldKey}
        disabled={disabled}
        fieldKey={fieldKey}
        hint={hint}
        hintIcon={hintIcon}
        onHintIconClick={onHintIconClick}
        label={mandatory ? <>{label} *</> : label}
        labelMaxWidth={labelMaxWidth}
        labelWidth={labelWidth}
        mandatory={mandatory}
        onLabelClick={this.onLabelClick}
        showDivider={showDivider}
        showLabel={showLabel}
        validateState={validateState}
        value={value}
      >
        <div style={styles.content}>
          {prependEl}{content}{appendEl}
        </div>
      </FormField>
    )
  }
}

TextFormField.propTypes = {
  autoFocus: propTypes.bool,
  autoFocusDelay: propTypes.number,
  disabled: propTypes.bool,
  errorText: propTypes.string,
  fieldKey: propTypes.string,
  focusEndOfField: propTypes.bool,
  formFieldClassName: propTypes.string,
  hint: propTypes.string,
  hintIcon: propTypes.string,
  immediate: propTypes.bool,    // true = onChange fires on text entry, false = onChange fires on blur
  label: propTypes.any,
  labelMaxWidth: propTypes.number,
  labelWidth: propTypes.number,
  mandatory: propTypes.bool,
  multiLine: propTypes.bool,
  onBlur: propTypes.func,
  onChange: propTypes.func,
  onClick: propTypes.func,
  onFocus: propTypes.func,
  onHintIconClick: propTypes.func,
  onMask: propTypes.func,
  options: propTypes.shape({
    prepend: propTypes.any,
    append: propTypes.any,
    length: propTypes.number,
  }),
  password: propTypes.bool,
  placeholder: propTypes.string,
  readOnly: propTypes.bool,
  rows: propTypes.number,
  rowsMax: propTypes.number,
  showDivider: propTypes.bool,
  showLabel: propTypes.bool,
  validateState: propTypes.bool,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
  ]),
}

TextFormField.defaultProps = {
  autoFocusDelay: 0,
  disabled: false,
  formFieldClassName: '',
  immediate: true,
  password: false,
  placeholder: '',
  readOnly: false,
  showLabel: true,
  validateState: false,
}


class StringField extends Component {
  focus = () => {
    if (this.input)
      this.input.focus()
  }

  render() {
    return (
      <TextFormField
        ref={e => this.input = e}
        onFocus={this.focus}
        {...this.props}/>
    )
  }
}

StringField.propTypes = {
  formFieldClassName: propTypes.string,
  label: propTypes.node,
  value: propTypes.string,
  onChange: propTypes.func,
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  immediate: propTypes.bool,
  validateState: propTypes.bool,
  autoFocusDelay: propTypes.number,
}

StringField.defaultProps = {
  formFieldClassName: '',
  readOnly: false,
  disabled: false,
  immediate: true,
}

class PhoneField extends Component {
  focus = () => {
    if (this.input)
      this.input.focus()
  }

  render() {
    const {value, readOnly} = this.props
    if (readOnly && value) {
      return <LabelField
        {...this.props}
        value={<a href={`tel:${value}`}>{value}</a>}/>
    }
    return <TextFormField
      {...this.props}
      ref={e => this.input = e}/>
  }
}

PhoneField.propTypes = {
  label: propTypes.node,
  value: propTypes.string,
  onChange: propTypes.func,
  readOnly: propTypes.bool,
}

PhoneField.defaultProps = {
  readOnly: false,
  disabled: false,
  password: false,
}

class EmailField extends Component {
  focus = () => {
    if (this.input)
      this.input.focus()
  }

  render() {
    const {value, readOnly} = this.props
    if (readOnly) {
      return <LabelField
        {...this.props}
        value={
          value
            ? <a href={`mailto:${value}`}>{value}</a>
            : null
        }/>
    }
    return <TextFormField
      {...this.props}
      ref={e => this.input = e}/>
  }
}

EmailField.propTypes = {
  label: propTypes.node,
  value: propTypes.string,
  onChange: propTypes.func,
  readOnly: propTypes.bool,
}

EmailField.defaultProps = {
  readOnly: false,
  disabled: false,
  password: false,
}

class BooleanField extends Component {
  onChange = (e, value) => {
    this.props.onChange(value)
  }

  render() {
    const {
      disabled,
      fieldKey,
      formFieldClassName,
      formFieldLabel,
      hint,
      label,
      labelWidth,
      mandatory,
      readOnly,
      showDivider,
      showLabel,
      value,
      valueWidth,
      width,
    } = this.props

    const displayLabel = mandatory ? <>{label} *</> : label

    if (readOnly) {
      return <LabelField
        label={displayLabel}
        labelWidth={labelWidth}
        value={value ? 'Yes' : 'No'}
        valueWidth={valueWidth}
        width={width}
      />
    }

    return (
      <FormField
        active={false}
        className={formFieldClassName || fieldKey}
        disabled={disabled}
        fieldKey={fieldKey}
        hint={hint}
        label={formFieldLabel}
        labelWidth={labelWidth}
        showDivider={showDivider}
        showLabel={showLabel}
        width={width}
      >
        <FormControlLabel
          classes={{root: 'formField-toggleControl-root'}}
          control={<Switch
            classes={{root: 'formField-toggleSwitch-root'}}
            checked={value}
            onChange={this.onChange}
            disabled={disabled}
          />}
          label={displayLabel}
        />
      </FormField>
    )
  }
}

BooleanField.propTypes = {
  disabled: propTypes.bool,
  fieldKey: propTypes.string,
  formFieldClassName: propTypes.string,
  formFieldLabel: propTypes.string,
  hint: propTypes.string,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.node,
  ]).isRequired,
  labelWidth: propTypes.number,
  mandatory: propTypes.bool,
  onChange: propTypes.func,
  readOnly: propTypes.bool,
  showDivider: propTypes.bool,
  showLabel: propTypes.bool,
  value: propTypes.bool,
  valueKey: propTypes.string,
  valueWidth: propTypes.number,
  width: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
  ]),
}

BooleanField.defaultProps = {
  formFieldClassName: '',
  formFieldLabel: '',
  readOnly: false,
  showDivider: true,
}


require('./listField.scss')

class ListField extends Component {
  constructor(props) {
    super(props)
    this.state = {
      value: this.extToInt(this.props.value),
      focused: false,
    }
  }

  componentDidMount() {
    this.setState({
      value: this.extToInt(this.props.value),
    })
  }

  parseValue = (value, values) => {
    if (typeof value === 'string' && values && values.length && typeof values[0].key === 'number') {
      const intValue = parseInt(value)
      if (!isNaN(intValue))
        value = intValue
    }

    return value
  }

  getMultiListValue = (value, values) => {
    if (typeof value === 'string')
      value = value.split(';')

    const ret = []
    value.forEach(v => {
      const option = values.find(option => option.key === v)
      if (option) {
        option.value = option.key
        ret.push(option)
      }
    })

    return ret
  }

  extToInt = (value) => {
    const {values, multi} = this.props

    let ret = value
    value = this.parseValue(value, values)

    if (isNil(ret))
      return null

    if (multi) {
      return this.getMultiListValue(value, values)
    } else if (values) {
      ret = values.find(opt => opt.key === value)
    }

    return ret
  }

  intToExt = (state) => {
    let ret = state
    if (ret !== null) {
      const {multi} = this.props
      if (multi) {
        ret = state.map((v) => {
          return v.value
        })
      } else {
        ret = state.value
      }
    }
    return ret
  }

  componentDidUpdate = (prevProps) => {
    if (prevProps.value !== this.props.value || !isEqual(prevProps.values, this.props.values)) {
      const value = this.extToInt(this.props.value)
      this.setState({value})
    }
  }

  onFocus = () => {
    this.setState({focused: true})
  }

  onBlur = () => {
    this.setState({focused: false})
  }

  onChange = (value) => {
    this.setState({value})
    const {values, onChange, isTreeSearcher} = this.props

    if (isTreeSearcher) {
      onChange(value)
    } else {
      const v = values && value ? values.find(v => v.key === value.value) : null
      onChange(this.intToExt(value), v || value)
    }
  }

  onLabelClick = () => {
    if (this.focus)
      this.focus.focus()
  }

  getListField = (value, disabled, errorText) => {
    const {
      fieldKey,
      multi,
      creatable,
      promptTextCreator,
      onNewOptionClick,
      backspaceRemoves,
      deleteRemoves,
      mandatory,
      isOptionDisabled,
      OptionRenderer,
      ValueRenderer,
      menuPlacement,
      validateState,
    } = this.props
    let {values, placeholder, clearable} = this.props

    if (isEmpty(values) || isNil(values)) values = []
    value = (isNil(value)) ? null : value
    errorText = errorText
      ? <div className={getClassName('listField-errorText')}>
        {errorText}
      </div>
      : null
    clearable = mandatory ? false : clearable
    placeholder = mandatory && isEmpty(placeholder) ? 'Required' : placeholder

    return (
      <div className="listFieldContainer">
        <SelectField
          name={fieldKey}
          id={fieldKey}
          ref={e => {
            this.focus = e
          }}
          placeholder={placeholder}
          clearable={clearable}
          onChange={this.onChange}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          options={values.map(v => ({value: v.key, ...v}))}
          value={value}
          multi={multi}
          disabled={disabled}
          creatable={creatable}
          menuPlacement={menuPlacement}
          promptTextCreator={promptTextCreator}
          onNewOptionClick={onNewOptionClick}
          backspaceRemoves={backspaceRemoves}
          deleteRemoves={deleteRemoves}
          isOptionDisabled={isOptionDisabled}
          OptionRenderer={OptionRenderer}
          ValueRenderer={ValueRenderer}
          {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
          {...getSelectFieldProps()}
        />
      </div>
    )
  }

  render() {
    const {
      fieldKey,
      formFieldClassName,
      mandatory,
      multi,
      readOnly,
      disabled,
      onAdd,
      onNav,
      addHint,
      navHint,
      navIcon,
      showLabel,
      label,
      labelWidth,
      valueWidth,
      hint,
      hintIcon,
      width,
      errorText,
      showDivider,
      validateState,
    } = this.props
    const {value, focused} = this.state

    let addElement, navElement
    if (onNav) {
      navElement = (
        <div
          className="listField-navButton"
          onClick={onNav}
          title={navHint}>
          <Icon name={navIcon ? navIcon : 'arrow'}/>
        </div>
      )
    }

    if (readOnly) {
      const labelValue = value ? value.label : ''
      return <>
        <LabelField
          label={label}
          value={labelValue}
          labelWidth={labelWidth}
          valueWidth={valueWidth}
          width={width}/>
        {navElement}
      </>
    }

    if (onAdd) addElement =
      <div className="listField-addButton" onClick={onAdd} title={addHint}>
        <Icon name="plus"/>
      </div>

    let className = 'listField'
    if (multi) className += ' listField-multi'

    return (
      <FormField
        fieldKey={fieldKey}
        className={formFieldClassName || fieldKey}
        label={mandatory ? <>{label} *</> : label}
        active={focused}
        disabled={disabled}
        showLabel={showLabel}
        showDivider={showDivider}
        labelWidth={labelWidth}
        hint={hint}
        hintIcon={hintIcon}
        width={width}
        onLabelClick={this.onLabelClick}
        mandatory={mandatory}
        validateState={validateState}
        value={value}>
        <div className={className}>
          {this.getListField(value, disabled, errorText, validateState)}
          {addElement}
          {navElement}
        </div>
      </FormField>
    )
  }
}

ListField.propTypes = {
  formFieldClassName: propTypes.string,
  fieldKey: propTypes.string,
  label: propTypes.node,
  hint: propTypes.string,
  hintIcon: propTypes.string,
  errorText: propTypes.string,
  value: propTypes.oneOfType([
    propTypes.string, // for non-multi
    propTypes.number, // for non-multi
    propTypes.array,  // for multi
    propTypes.object, // for duration-picker
  ]),
  readOnly: propTypes.bool,
  labelWidth: propTypes.number,
  valueWidth: propTypes.number,
  mandatory: propTypes.bool,
  disabled: propTypes.bool,
  multi: propTypes.bool,
  showDivider: propTypes.bool,
  values: propTypes.array,
  placeholder: propTypes.string,
  menuPlacement: propTypes.oneOf(['auto', 'top', 'bottom']),
  onChange: propTypes.func,
  onAdd: propTypes.func,
  onNav: propTypes.func,
  addHint: propTypes.string,
  navHint: propTypes.string,
  navIcon: propTypes.string,
  className: propTypes.string,
  showLabel: propTypes.bool,
  clearable: propTypes.bool,
  isTreeSearcher: propTypes.bool,
  isOptionDisabled: propTypes.func,
  OptionRenderer: propTypes.func,
  ValueRenderer: propTypes.func,
  validateState: propTypes.bool,
}

ListField.defaultProps = {
  formFieldClassName: '',
  readOnly: false,
  disabled: false,
  multi: false,
  showDivider: true,
  placeholder: '',
  backspaceRemoves: false,
  clearable: true,
  isTreeSearcher: false,
  OptionRenderer: null,
  ValueRenderer: null,
  validateState: false,
}

class AsyncListField extends ListField {
  getListField = (value, disabled, errorText) => {
    const {fieldKey, className, mandatory, multi, placeholder, loadOptions} = this.props
    if (errorText) errorText = <div className="listField-mandatoryLabel">{errorText}</div>

    return (
      <div className="listFieldContainer">
        <SelectField
          async
          name={fieldKey}
          id={fieldKey}
          className={className}
          ref={e => {
            this.focus = e
          }}
          placeholder={placeholder}
          clearable={!mandatory}
          onChange={this.onChange}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          loadOptions={loadOptions}
          value={value}
          multi={multi}
          disabled={disabled}
          {...getSelectFieldProps()}
        />
        {errorText}
      </div>
    )
  }
}

AsyncListField.propTypes = {
  ...ListField.propTypes,
  loadOptions: propTypes.func,
}

class CompanyListField extends ListField {
  getListField = (value, disabled, errorText, validateState) => {
    const {
      mandatory, fields, placeholder, autoFocus,
      creatable = false, promptTextCreator, onAdd,
      optionRenderer, isOptionDisabled,
    } = this.props

    return (
      <CompanySelectField
        ref={e => {
          this.focus = e
        }}
        id={'standard-helperText'}
        required={mandatory}
        clearable={!mandatory}
        onChange={this.onChange}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        value={value}
        disabled={disabled}
        fields={fields}
        autoFocus={autoFocus}
        creatable={creatable}
        onNewOptionClick={onAdd}
        OptionRenderer={optionRenderer}
        isOptionDisabled={isOptionDisabled}
        promptTextCreator={promptTextCreator}
        {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
        {...getSelectFieldProps()}
      />
    )
  }
}

CompanyListField.propTypes = {
  label: propTypes.node.isRequired,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.shape({
      value: propTypes.any,
      label: propTypes.node,
      clearable: propTypes.bool,
    }),
  ]),
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  creatable: propTypes.bool,
  onChange: propTypes.func,
  placeholder: propTypes.string,
}

CompanyListField.defaultProps = {
  placeholder: '',
}

class PackTypeListField extends ListField {

  render() {
    const {
      fieldKey, formFieldClassName, disabled, showLabel, labelWidth, hint, value, mandatory,
      placeholder, style, autoFocus, creatable = false, promptTextCreator, values, validateState,
      errorText,
    } = this.props
    const {label} = this.props
    const requiredFieldProps = getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)

    return (
      <FormField
        fieldKey={fieldKey}
        className={formFieldClassName || fieldKey}
        label={mandatory ? <>{label} *</> : label}
        disabled={disabled}
        showLabel={showLabel}
        labelWidth={labelWidth}
        hint={hint}
        onLabelClick={this.onLabelClick}
        mandatory={mandatory}
        validateState={validateState}
        value={value}>
        <div className="listField">
          <div className="listFieldContainer">
            <SelectField
              ref={e => {
                this.focus = e
              }}
              name={fieldKey}
              id={fieldKey}
              clearable={!mandatory}
              onChange={this.onChange}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              value={value}
              options={values.map(v => ({value: v.key, ...v}))}
              required={mandatory}
              disabled={disabled}
              autoFocus={autoFocus}
              style={style}
              creatable={creatable}
              promptTextCreator={promptTextCreator}
              placeholder={requiredFieldProps.hintText}
              {...requiredFieldProps}
              {...getSelectFieldProps()}
            />
          </div>
        </div>
      </FormField>
    )
  }
}

PackTypeListField.propTypes = {
  label: '',
  placeholder: '',
}

PackTypeListField.propTypes = {
  label: propTypes.node.isRequired,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.shape({
      value: propTypes.any,
      label: propTypes.node,
      clearable: propTypes.bool,
    }),
  ]),
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  creatable: propTypes.bool,
  onChange: propTypes.func,
}

class PartCategoryListField extends ListField {
  getListField = (value, disabled, errorText, validateState) => {
    const {
      mandatory,
      fields,
      placeholder,
      autoFocus,
      creatable,
      promptTextCreator,
      params,
    } = this.props

    return (
      <PartCategorySelectField
        ref={e => {
          this.focus = e
        }}
        clearable={!mandatory}
        onChange={this.onChange}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        value={value}
        disabled={disabled}
        fields={fields}
        autoFocus={autoFocus}
        creatable={creatable}
        promptTextCreator={promptTextCreator}
        params={params}
        onCheck={this.props.onChange}
        {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
        {...getSelectFieldProps()}
      />
    )
  }
}

PartCategoryListField.propTypes = {
  label: propTypes.node.isRequired,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.shape({
      value: propTypes.any,
      label: propTypes.node,
      clearable: propTypes.bool,
    }),
  ]),
  placeholder: propTypes.string,
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  creatable: propTypes.bool,
  onChange: propTypes.func,
  params: propTypes.object,
}

PartCategoryListField.defaultProps = {
  creatable: false,
  placeholder: '',
  params: {},
}

class PartListField extends ListField {
  extToInt = (value) => {
    return value
  }

  intToExt = (state) => {
    if (isNil(state)) return null
    return state
  }

  getListField = (value, disabled, errorText, validateState) => {
    const {
      mandatory,
      placeholder,
      params,
      user,
      isOptionDisabled,
      allowedTypes,
      OptionRenderer,
      ValueRenderer,
    } = this.props

    return (
      <PartSelectField
        ref={e => {
          this.focus = e
        }}
        clearable={!mandatory}
        onChange={this.onChange}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        value={value}
        disabled={disabled}
        params={params}
        user={user}
        allowedTypes={allowedTypes}
        placeholder={placeholder || "Required"}
        isOptionDisabled={isOptionDisabled}
        OptionRenderer={OptionRenderer}
        ValueRenderer={ValueRenderer}
        {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
        {...getSelectFieldProps()}
      />
    )
  }
}

PartListField.propTypes = {
  disabled: propTypes.bool,
  isOptionDisabled: propTypes.func,
  label: propTypes.node.isRequired,
  mandatory: propTypes.bool,
  onChange: propTypes.func,
  OptionRenderer: propTypes.func,
  params: propTypes.object,
  allowedTypes: propTypes.arrayOf(propTypes.number),
  placeholder: propTypes.string,
  readOnly: propTypes.bool,
  user: propTypes.object,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.shape({
      value: propTypes.any.isRequired,
      label: propTypes.node.isRequired,
    }),
  ]),
  ValueRenderer: propTypes.func,
}

PartListField.defaultProps = {
  params: {},
  placeholder: '',
}

class ContactListField extends ListField {
  getListField = (value, disabled, errorText, validateState) => {
    const {
      mandatory,
      companyId,
      dormants,
      fields,
      params,
      creatable,
      showDivider,
      placeholder,
      promptTextCreator,
      multi,
      optionRenderer,
      isOptionDisabled,
      clearable,
    } = this.props

    const isClearable = !isNil(clearable) ? clearable : !mandatory

    return (
      <ContactSelectField
        ref={e => {
          this.focus = e
        }}
        clearable={isClearable}
        onChange={this.onChange}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        value={value}
        disabled={disabled}
        companyId={companyId}
        dormants={dormants}
        fields={fields}
        params={params}
        creatable={creatable}
        promptTextCreator={promptTextCreator}
        multi={multi}
        showDivider={showDivider}
        isOptionDisabled={isOptionDisabled}
        OptionRenderer={optionRenderer}
        {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
        {...getSelectFieldProps()}
      />
    )
  }
}

ContactListField.propTypes = {
  label: propTypes.node.isRequired,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.shape({
      value: propTypes.any.isRequired,
      label: propTypes.node.isRequired,
    }),
    propTypes.arrayOf(
      propTypes.oneOfType([
        propTypes.string,
        propTypes.number,
        propTypes.shape({
          value: propTypes.any.isRequired,
          label: propTypes.node.isRequired,
        }),
      ])
    ),
  ]),
  placeholder: propTypes.string,
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  multi: propTypes.bool,
  clearable: propTypes.bool,
  showDivider: propTypes.bool,
  onChange: propTypes.func,
  isOptionDisabled: propTypes.func,
  optionRenderer: propTypes.func,
}

ContactListField.defaultProps = {
  placeholder: '',
  creatable: false,
  multi: false,
  showDivider: true,
  optionRenderer: null,
}

class OpportunityListField extends ListField {
  getListField = (value, disabled, errorText, validateState) => {
    const {mandatory, companyId, placeholder, fields} = this.props

    return (
      <OpportunitySelectField
        ref={e => {
          this.focus = e
        }}
        clearable={!mandatory}
        onChange={this.onChange}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        value={value}
        disabled={disabled}
        companyId={companyId}
        fields={fields}
        {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
        {...getSelectFieldProps()}
      />
    )
  }
}

OpportunityListField.propTypes = {
  label: propTypes.node.isRequired,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.shape({
      value: propTypes.any.isRequired,
      label: propTypes.node.isRequired,
    }),
  ]),
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  onChange: propTypes.func,
  placeholder: propTypes.string,
}

OpportunityListField.defaultProps = {
  placeholder: '',
}

class ReferenceListField extends ListField {
  getListField = (value, disabled, errorText, validateState) => {
    const {mandatory, companyId, contactId, placeholder, fields, params} = this.props

    return (
      <ReferenceSelectField
        ref={e => {
          this.focus = e
        }}
        clearable={!mandatory}
        onChange={this.onChange}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        value={value}
        disabled={disabled}
        companyId={companyId}
        contactId={contactId}
        fields={fields}
        multi={false}
        params={params}
        {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
        {...getSelectFieldProps()}
      />
    )
  }
}

ReferenceListField.propTypes = {
  label: propTypes.node.isRequired,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.shape({
      value: propTypes.any.isRequired,
      label: propTypes.node.isRequired,
    }),
  ]),
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  onChange: propTypes.func,
  placeholder: propTypes.string,
}

ReferenceListField.defaultProps = {
  placeholder: '',
}

class OrderListField extends ListField {

  getOptionRenderer({option}) {
    return (
      <div className={'option-container'}>
        <span className={option.className}>{option.status}</span><span
        className={'option-label'}>{option.label}</span>
      </div>
    )
  }

  getListField = (value, disabled, placeholder, errorText) => {
    const {
      mandatory,
      companyId,
      fields,
      autoFocus,
      params,
      validateState,
    } = this.props

    return (
      <OrderSelectField
        ref={e => {
          this.focus = e
        }}
        clearable={!mandatory}
        onChange={this.onChange}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        value={value}
        disabled={disabled}
        companyId={companyId}
        fields={fields}
        params={params}
        autoFocus={autoFocus}
        placeholder={placeholder || this.props.placeholder}
        OptionRenderer={this.getOptionRenderer}
        {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
        {...getSelectFieldProps()}
      />
    )
  }
}

OrderListField.propTypes = {
  label: propTypes.node.isRequired,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.object,
  ]),
  params: propTypes.object,
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  onChange: propTypes.func,
  placeholder: propTypes.string,
}

OrderListField.defaultProps = {
  params: {
    sortColumn: 'status',
    sortDirection: 'asc',
    start: 0,
    limit: 10,
    status: [QuoteStatus.draft, QuoteStatus.quoted, QuoteStatus.rejected],
  },
  placeholder: '',
}

class PurchaseOrderListField extends ListField {

  getOptionRenderer({option}) {
    return (
      <div className={'option-container'}>
        <span className={option.className}>{option.status}</span><span
        className={'option-label'}>{option.label}</span>
      </div>
    )
  }

  getListField = (value, disabled, placeholder, errorText) => {
    const {mandatory, companyId, fields, autoFocus, params, validateState} = this.props

    return (
      <PurchaseOrderSelectField
        ref={e => {
          this.focus = e
        }}
        clearable={!mandatory}
        onChange={this.onChange}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        value={value}
        disabled={disabled}
        companyId={companyId}
        fields={fields}
        params={params}
        autoFocus={autoFocus}
        OptionRenderer={this.getOptionRenderer}
        {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
        {...getSelectFieldProps()}
      />
    )
  }
}

PurchaseOrderListField.propTypes = {
  label: propTypes.node.isRequired,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.object,
  ]),
  params: propTypes.object,
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  onChange: propTypes.func,
  placeholder: propTypes.string,
  validateState: propTypes.bool,
}

PurchaseOrderListField.defaultProps = {
  params: {
    sortColumn: 'ref',
    sortDirection: 'asc',
  },
  placeholder: '',
}


class DurationPicker extends ListField {

  state = {
    selectorVisible: false,
  }

  showSelector = () => {
    this.setState({selectorVisible: true})
  }

  hideSelector = () => {
    this.setState({selectorVisible: false})
  }


  getStyles = () => {
    return {
      container: {
        display: 'flex',
        justifyContent: 'flex-start',
      },
      durationDropDown: {
        width: '25%',
        paddingTop: 4,
      },
      durationNumberField: {
        width: 60,
      },
      durationAppendText: {
        paddingTop: 10,
      },
    }
  }

  onEnabledChange = (e, enabled) => {
    const {value, defaultPeriod, defaultDuration, defaultEndDate, onChange} = this.props
    if (value) {
      onChange({
        enabled,
        period: defaultPeriod.value,
        duration: defaultDuration,
        repeatEndDate: defaultEndDate,
      })
    } else {
      onChange({
        ...value,
        enabled,
      })
    }
  }

  onPeriodChange = (period) => {
    const {value, onChange, defaultEndDate} = this.props
    onChange({
      ...value,
      period: period.value,
      repeatEndDate: defaultEndDate,
    })
  }

  onDurationChange = (duration) => {
    const {value, onChange, defaultEndDate} = this.props
    onChange({
      ...value,
      duration,
      repeatEndDate: defaultEndDate,
    })
  }

  getOptions = () => {
    const {useHoursAndMins, minPeriod, maxPeriod} = this.props
    if (minPeriod || maxPeriod) {
      return Object.values(PeriodUnit).filter(unit =>
        (isNil(minPeriod) || unit.order >= minPeriod.order) &&
        (isNil(maxPeriod) || unit.order <= maxPeriod.order))
    }
    return useHoursAndMins === false ? periodUnitsDaysToYears : periodUnitsMinsToMonths
  }

  render() {
    const {fieldKey, value, label, readOnly, disabled, suffix, showDivider} = this.props
    const {selectorVisible} = this.state
    const styles = this.getStyles()
    const options = this.getOptions()

    const fieldLabel = isNil(value.enabled)
      ? label
      : <FormControlLabel
        classes={{root: 'formField-toggleControl-root'}}
        control={<Switch
          classes={{root: 'formField-toggleSwitch-root'}}
          checked={value && value.enabled}
          onChange={this.onEnabledChange}
        />}
        label={label}
      />

    return (
      <FormField
        className={fieldKey}
        fieldKey={fieldKey}
        label={fieldLabel}
        showDivider={showDivider}
        active={selectorVisible}
        readOnly={readOnly}>
        {value.enabled !== false &&
          <div style={styles.container}>
            <div className="durationNumberField" style={styles.durationNumberField}>
              <NumberComponentField
                onChange={this.onDurationChange}
                onFocus={this.showSelector}
                onBlur={this.hideSelector}
                value={value ? value.duration : null}
                options={{min: 1}}
                readOnly={readOnly}
              />
            </div>
            <div className="durationDropDown" style={styles.durationDropDown}>
              <SelectField
                options={options}
                searchable={false}
                onChange={this.onPeriodChange}
                showBorder={false}
                value={value ? value.period : null}
                disabled={disabled}
                underlineShow={false}
                onOpen={this.showSelector}
                onClose={this.hideSelector}
                clearable={false}
                readOnly={readOnly}
                {...getSelectFieldProps()}
              />
            </div>
            <div className="durationAppendText" style={styles.durationAppendText}> {suffix} </div>
          </div>
        }
      </FormField>
    )
  }
}

DurationPicker.propTypes = {
  fieldKey: propTypes.string,
  value: propTypes.shape({
    enabled: propTypes.bool,
    duration: propTypes.number,
    period: propTypes.string,
  }),
  label: propTypes.node,
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  suffix: propTypes.node,
  useHoursAndMins: propTypes.bool,
  defaultPeriod: propTypes.object,
  defaultDuration: propTypes.number,
  defaultEndDate: propTypes.number,
  onChange: propTypes.func.isRequired,
}

DurationPicker.defaultProps = {
  defaultPeriod: PeriodUnit.minutes,
  defaultDuration: 15,
  defaultEndDate: new Date().getTime(),
}


class RepeatDaysField extends ListField {

  dayOptions = [
    {id: 1, value: 1, label: t('Mon')},
    {id: 2, value: 2, label: t('Tue')},
    {id: 3, value: 3, label: t('Wed')},
    {id: 4, value: 4, label: t('Thu')},
    {id: 5, value: 5, label: t('Fri')},
    {id: 6, value: 6, label: t('Sat')},
    {id: 0, value: 0, label: t('Sun')},
  ]

  selectChanged = (value, checked) => {
    let selected = this.props.value
    if (checked === true) {
      selected = selected.concat([value])
    } else {
      selected = selected.filter(item => item !== value)
    }
    this.props.onChange(selected)
  }

  render() {
    const {fieldKey, value, label, readOnly} = this.props
    const dayCheckboxes = this.dayOptions.map((dayOption) => {
      return (
        <FormControlLabel
          key={dayOption.id}
          className={'dayOption-type-child'}
          style={{
            width: 80,
            zoom: 0.8,
            marginRight: 8,
          }}
          control={
            <Checkbox
              checked={!!value.includes(dayOption.id)}
              onChange={(e, selected) => this.selectChanged(dayOption.id, selected)}
              readOnly={readOnly}
            />
          }
          label={<span style={{marginLeft: -12}}>{dayOption.label}</span>}
        />
      )
    })

    return (
      <FormField fieldKey={fieldKey} className={fieldKey} label={label}>
        <div>
          {dayCheckboxes}
        </div>
      </FormField>
    )
  }
}


class RepeatMonthsField extends ListField {
  selectChanged = (value) => {
    this.props.onChange(value ? value.value : null)
  }

  render() {
    const {fieldKey, value, label, disabled, date} = this.props
    const prefixes = [1, 2, 3, 4, 5]
    const weekDuration = ['First', 'Second', 'Third', 'Fourth', 'Last']
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

    const formatDate = new Date(date)
    const n = formatDate.getDate()
    const currentDay =
      n + (n > 0 ? ['th', 'st', 'nd', 'rd'][(n > 3 && n < 21) || n % 10 > 3 ? 0 : n % 10] : '')

    const dayName = dayNames[formatDate.getDay()]
    const weekText = weekDuration[prefixes[0 | (formatDate.getDate() - 1) / 7] - 1]

    const monthOptions = [
      {value: 1, label: 'The ' + currentDay + ' day of the month'},
      {value: 2, label: 'Repeat on the ' + weekText + ' ' + dayName + ' of the month'},
    ]

    return (
      <FormField fieldKey={fieldKey} className={fieldKey} label={label}>
        <div className="repeatMonthDropDown">
          <SelectField
            options={monthOptions}
            searchable={false}
            openOnFocus={false}
            onChange={this.selectChanged}
            showBorder={false}
            value={value}
            disabled={disabled}
            clearable={false}
            {...getSelectFieldProps()}
          />
        </div>
      </FormField>
    )
  }
}

class CheckBoxTextField extends Component {
  onChange = (e, value) => {
    this.props.onChange(value)
  }

  render() {
    const {
      fieldKey, formFieldClassName, label, formFieldLabel, value, readOnly,
      disabled, labelWidth, hint, showLabel, width,
      showDivider, postLabel, borderBottomWidth,
    } = this.props

    const styles = {
      checkboxStyle: {
        width: 550,
        zoom: 0.8,
        paddingTop: 12,
        marginRight: -8,
      },

      labelStyle: {
        marginLeft: -12,
        fontSize: 18,
      },
    }

    return (
      <FormField
        fieldKey={fieldKey}
        className={formFieldClassName || fieldKey}
        label={formFieldLabel}
        active={false}
        disabled={disabled}
        hint={hint}
        labelWidth={labelWidth}
        showLabel={showLabel}
        borderBottomWidth={borderBottomWidth}
        showDivider={showDivider}
        width={width}>
        <FormControlLabel
          style={styles.checkboxStyle}
          control={
            <Checkbox
              checked={value}
              onChange={this.onChange}
              disabled={disabled}
              readOnly={readOnly}
            />
          }
          label={<span style={styles.labelStyle}>{label}</span>}
        />
        <label
          style={{
            color: themeVars.palette.primaryFontColor,
          }}>{postLabel}</label>
      </FormField>
    )
  }
}

CheckBoxTextField.propTypes = {
  fieldKey: propTypes.string,
  formFieldClassName: propTypes.string,
  borderBottomWidth: propTypes.number,
  label: propTypes.node.isRequired,
  formFieldLabel: propTypes.string,
  hint: propTypes.string,
  value: propTypes.bool,
  disabled: propTypes.bool,
  labelWidth: propTypes.number,
  postLabel: propTypes.string,
  valueKey: propTypes.string,
  readOnly: propTypes.bool,
  showLabel: propTypes.bool,
  width: propTypes.oneOfType([propTypes.string, propTypes.number]),
  showDivider: propTypes.bool,
  onChange: propTypes.func,
}


CheckBoxTextField.defaultProps = {
  formFieldClassName: '',
  formFieldLabel: '',
  readOnly: false,
  showDivider: true,
}

class CheckBoxField extends Component {
  onChange = (e, value) => {
    this.props.onChange(value)
  }

  render() {
    const {
      fieldKey, formFieldClassName, label, formFieldLabel, value, readOnly,
      disabled, labelWidth, hint, showLabel, width, showDivider,
    } = this.props

    return (
      <FormField
        fieldKey={fieldKey}
        className={formFieldClassName || fieldKey}
        label={formFieldLabel}
        active={false}
        disabled={disabled}
        hint={hint}
        labelWidth={labelWidth}
        showLabel={showLabel}
        showDivider={showDivider}
        width={width}>
        <FormControlLabel
          style={{
            width: 550,
            zoom: 0.8,
            paddingTop: 12,
            marginRight: -8,
          }}
          control={
            <Checkbox
              checked={value}
              onChange={this.onChange}
              disabled={disabled}
              readOnly={readOnly}
            />
          }
          label={<span style={{marginLeft: -12, fontSize: 18}}>{label}</span>}
        />
      </FormField>

    )
  }
}

CheckBoxField.propTypes = {
  fieldKey: propTypes.string,
  formFieldClassName: propTypes.string,
  label: propTypes.node.isRequired,
  formFieldLabel: propTypes.string,
  hint: propTypes.string,
  value: propTypes.bool,
  disabled: propTypes.bool,
  labelWidth: propTypes.number,
  total: propTypes.number,
  remaining: propTypes.number,
  valueKey: propTypes.string,
  readOnly: propTypes.bool,
  showLabel: propTypes.bool,
  width: propTypes.oneOfType([propTypes.string, propTypes.number]),
  showDivider: propTypes.bool,
  hidden: propTypes.bool,
  onChange: propTypes.func,
}


CheckBoxField.defaultProps = {
  formFieldClassName: '',
  formFieldLabel: '',
  readOnly: false,
  showDivider: true,
}


class RepeatEndPeriodField extends ListField {
  constructor(props) {
    super(props)
    this.state = {
      selectorVisible: false,
      previousEndDate: null,
      previousOccurrences: null,
    }
  }

  endPeriodOptions = [
    {value: 1, label: 'On'},
    {value: 2, label: 'After'},
  ]

  showSelector = () => {
    this.setState({selectorVisible: true})
  }

  hideSelector = () => {
    this.setState({selectorVisible: false})
  }

  getStyles = () => {
    return {
      container: {
        display: 'flex',
      },
      datePicker: {
        paddingLeft: 20,
      },
      repeatPeriodDropDown: {
        paddingTop: 7,
        width: 100,
      },
      endOccurrence: {
        display: 'inline-block',
        width: 60,
      },
      endLabel: {
        display: 'inline-block',
      },
    }
  }

  getSelectValue = () => {
    const {value} = this.props
    if (value && !isNil(value.occurrences)) return 2
    return 1
  }

  getOccurrences = () => {
    const {value, defaultOccurrences} = this.props
    if (value && !isNil(value.occurrences)) return value.occurrences
    return defaultOccurrences
  }

  getEndDate = () => {
    const {value, defaultEndDate} = this.props
    if (value && !isNil(value.endDate)) return new Date(value.endDate)
    return new Date(defaultEndDate)
  }

  onChangeOption = (selectValue) => {
    const {value, defaultEndDate, defaultOccurrences, onChange} = this.props
    const {previousEndDate, previousOccurrences} = this.state
    this.setState({
      previousEndDate: value.endDate || previousEndDate,
      previousOccurrences: value.occurrences || previousOccurrences,
    })
    onChange({
      endDate: selectValue.value === 1 ? previousEndDate || defaultEndDate : null,
      occurrences: selectValue.value === 2 ? previousOccurrences || defaultOccurrences : null,
    })
  }

  onChangeDate = (e, value) => {
    const endDate = value.setHours(1) //offset time by an hour to ensure correct date is displayed
    this.props.onChange({
      endDate,
      occurrences: null,
    })
  }

  onChangeOccurrences = (occurrences) => {
    this.props.onChange({
      occurrences,
      endDate: null,
    })
  }

  render() {
    const {fieldKey, label, readOnly, disabled} = this.props
    const styles = this.getStyles()

    return (
      <FormField fieldKey={fieldKey} className={fieldKey} label={label}>
        <div style={styles.container}>
          <div className="repeatPeriodDropDown" style={styles.repeatPeriodDropDown}>
            <SelectField
              options={this.endPeriodOptions}
              searchable={false}
              openOnFocus={false}
              onChange={this.onChangeOption}
              showBorder={false}
              value={this.getSelectValue()}
              disabled={disabled}
              clearable={false}
              readOnly={readOnly}
              {...getSelectFieldProps()}
            />
          </div>
          {this.getSelectValue() === 1
            ?
            <div style={styles.container}>
              <div className="datePicker" style={styles.datePicker}>
                <DatePicker
                  fullWidth={true}
                  underlineShow={false}
                  onChange={this.onChangeDate}
                  onClick={this.showSelector}
                  onDismiss={this.hideSelector}
                  value={this.getEndDate()}
                  disabled={disabled}
                  onClose={this.hideSelector}
                  readOnly={readOnly}
                />
              </div>
            </div>
            :
            <div>
              <div className='endOccurrence' style={styles.endOccurrence}>
                <NumberComponentField
                  onChange={this.onChangeOccurrences}
                  onFocus={this.showSelector}
                  onBlur={this.hideSelector}
                  value={this.getOccurrences()}
                  readOnly={readOnly}
                />
              </div>
              <div className='endLabel' style={styles.endLabel}>occurrences</div>
            </div>
          }
        </div>
      </FormField>
    )
  }
}

RepeatEndPeriodField.propTypes = {
  label: propTypes.node,
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  value: propTypes.shape({
    endDate: propTypes.number,
    occurrences: propTypes.number,
  }),
  defaultEndDate: propTypes.number,
  defaultOccurrences: propTypes.number,
}

RepeatEndPeriodField.defaultProps = {
  defaultEndDate: new Date().getTime(),
  defaultOccurrences: 10,
}


class TimeSpentField extends ListField {
  timeSpentOptions = [
    {id: 1, value: 1, label: '1 Minute'},
    {id: 2, value: 5, label: '5 Minutes'},
    {id: 3, value: 10, label: '10 Minutes'},
    {id: 4, value: 15, label: '15 Minutes'},
    {id: 5, value: 20, label: '20 Minutes'},
    {id: 6, value: 25, label: '25 Minutes'},
    {id: 7, value: 30, label: '30 Minutes'},
    {id: 8, value: 35, label: '35 Minutes'},
    {id: 9, value: 40, label: '40 Minutes'},
    {id: 10, value: 45, label: '45 Minutes'},
    {id: 11, value: 50, label: '50 Minutes'},
    {id: 12, value: 55, label: '55 Minutes'},
    {id: 13, value: 60, label: '1 Hour'},
    {id: 14, value: 75, label: '1 Hour 15 Minutes'},
    {id: 15, value: 90, label: '1 Hour 30 Minutes'},
    {id: 16, value: 120, label: '2 Hours'},
    {id: 17, value: 150, label: '2 Hours 30 Minutes'},
    {id: 18, value: 180, label: '3 Hours'},
    {id: 19, value: 240, label: '4 Hours'},
    {id: 20, value: 300, label: '5 Hours'},
    {id: 21, value: 360, label: '6 Hours'},
    {id: 22, value: 420, label: '7 Hours'},
    {id: 23, value: 480, label: '8 Hours'},
  ]

  getStyles = () => {
    return {
      container: {
        display: 'flex',
      },
      timeSpentDropDown: {
        width: '85%',
      },
    }
  }

  onChangeOption = (value) => {
    this.props.onChange(value)
  }

  onChange = (e, value) => {
    this.props.onChange(value ? value.getTime() : null)
  }


  render() {
    const {fieldKey, value, label, disabled} = this.props
    const styles = this.getStyles()
    return (
      <FormField fieldKey={fieldKey} className={fieldKey} label={label}>
        <div style={styles.container}>
          <div className="timeSpent" style={styles.timeSpentDropDown}>
            <SelectField
              options={this.timeSpentOptions}
              searchable={false}
              openOnFocus={false}
              onChange={this.onChangeOption}
              showBorder={false}
              value={value}
              disabled={disabled}
              {...getSelectFieldProps()}
            />
          </div>
        </div>
      </FormField>
    )
  }
}


class OrderSelectorField extends Component {
  constructor(props) {
    super(props)

    this.state = {
      selectorVisible: props.autoFocus,
    }
  }

  onShowSelector = () => {
    this.setState({
      selectorVisible: true,
    })
  }

  onHideSelector = () => {
    this.setState({
      selectorVisible: false,
    })
  }

  onChange = (orderId, order) => {
    this.props.onChange(order)
  }

  selectChanged = (value) => {
    if (value == null) this.props.onChange(null)
  }

  render() {
    const {
      fieldKey, label, readOnly, disabled, mandatory, placeholder, errorText,
      selectorTitle, validateState,
    } = this.props

    const {selectorVisible} = this.state

    let {value} = this.props
    if (value) value = {value: value.id, label: value.ref + ': ' + value.desc}
    if (readOnly) return <LabelField label={label} value={value ? value.label : ''}/>

    return (
      <FormField
        fieldKey={fieldKey}
        className={fieldKey}
        label={label}
        active={selectorVisible}
        onLabelClick={this.onShowSelector}
        disabled={readOnly}
        mandatory={mandatory}
        validateState={validateState}
        value={value}>
        <div className="listField" ref={el => this.selectorAnchor = el}>
          <SelectField
            clearable={!mandatory}
            onOpen={this.onShowSelector}
            onChange={this.selectChanged}
            searchable={false}
            openOnFocus={false}
            options={[]}
            value={value}
            disabled={disabled}
            {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
            {...getSelectFieldProps()}
          />
        </div>
        <QuoteFinder
          anchorElement={this.selectorAnchor}
          visible={selectorVisible}
          onClose={this.onHideSelector}
          submitLabel={selectorTitle}
          allowLineItemsToggle={true}
          selectLineItems={false}
          onSubmit={this.onChange}
          orderPivotTableKey={quotingContextSelectorOrdersPivotTableKey}
        />
      </FormField>
    )
  }
}

OrderSelectorField.propTypes = {
  autoFocus: propTypes.bool,
  fieldKey: propTypes.string,
  label: propTypes.node.isRequired,
  value: propTypes.object,
  readOnly: propTypes.bool,
  mandatory: propTypes.bool,
  placeholder: propTypes.string,
  errorText: propTypes.string,
  disabled: propTypes.bool,
  onChange: propTypes.func,
  repeatQuoteComponent: propTypes.bool,
  hideRepeatQuoteComponent: propTypes.func,
  showRepeatQuoteComponent: propTypes.func,
  validateState: propTypes.bool,
  selectorTitle: propTypes.string,
}

OrderSelectorField.defaultProps = {
  autoFocus: false,
  readOnly: false,
  disabled: false,
  placeholder: '',
  selectorTitle: 'Repeat This Quote',
}

class MultiListField extends Component {
  render() {
    return (
      <ListField {...this.props} multi/>
    )
  }
}

class NumberField extends Component {
  constructor(props) {
    super(props)
    this.state = {
      isFocused: false,
      localValue: props.value,
    }
    this.getClass = prefix('numberField-')
  }

  onFocus = () => {
    this.setState({isFocused: true})
  }

  onBlur = () => {
    this.setState({isFocused: false})
  }

  onMask = (value) => {
    const {options} = this.props
    if (value && options && options.accuracy) value = formatRoundedNumber(Number(value), options.accuracy)
    return value
  }

  onChange = (value) => {
    this.setState({localValue: value})
    const {onChange} = this.props
    onChange(value)
  }

  onLabelClick = () => {
    this.input.focus()
  }

  render() {
    const {
      formFieldClassName,
      fieldKey,
      label,
      showLabel,
      options,
      readOnly,
      disabled,
      fullWidth,
      width,
      placeholder,
      value,
      debounce,
      labelWidth,
      hint,
      hintIcon,
      showDivider,
      preventClear,
      defaultValue,
    } = this.props

    const {isFocused} = this.state

    if (readOnly) {
      const maskedValue = this.onMask(value)
      const prepend = options && options.prepend ? options.prepend : ''
      const append = options && options.append ? options.append : ''
      const accuracy = options && !isNil(options.accuracy) ? options.accuracy : 2
      const roundedValue = isNil(maskedValue)
        ? null
        : formatRoundedNumber(round(maskedValue, accuracy), accuracy)
      return <LabelField
        fieldKey={fieldKey}
        label={label}
        value={roundedValue}
        prepend={prepend}
        append={append}
        labelWidth={labelWidth}
        showLabel={showLabel}
        valueAlign="right"/>
    }

    const {mandatory, validateState} = this.props

    return (
      <FormField
        fieldKey={fieldKey}
        className={formFieldClassName || fieldKey}
        label={mandatory ? <>{label} *</> : label}
        disabled={disabled}
        active={isFocused}
        labelWidth={labelWidth}
        showLabel={showLabel}
        hint={hint}
        hintIcon={hintIcon}
        showDivider={showDivider}
        mandatory={mandatory}
        validateState={validateState}
        value={value}
        onLabelClick={this.onLabelClick}
      >
        <>
          <NumberComponentField
            fieldKey={fieldKey}
            ref={e => this.input = e}
            onChange={this.onChange}
            value={value}
            options={options}
            debounce={debounce}
            fullWidth={fullWidth}
            width={width}
            style={{marginTop: 1}}
            disabled={disabled}
            onMask={this.onMask}
            placeholder={mandatory ? 'Required' : placeholder}
            preventClear={preventClear}
            defaultValue={defaultValue}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
          />
          {isFieldRequired(validateState, mandatory, this.state.localValue) &&
            <div
              style={{
                position: 'relative',
                bottom: 2,
                fontSize: 12,
                lineHeight: '12px',
                color: 'rgb(244, 67, 54)',
              }}
            >
              This field is mandatory
            </div>
          }
        </>
      </FormField>
    )
  }
}

NumberField.propTypes = {
  formFieldClassName: propTypes.string,
  fieldKey: propTypes.string,
  label: propTypes.node,
  hint: propTypes.string,
  hintIcon: propTypes.string,
  mandatory: propTypes.bool,
  validateState: propTypes.bool,
  options: propTypes.shape({
    prepend: propTypes.oneOfType([propTypes.element, propTypes.string]),
    append: propTypes.oneOfType([propTypes.element, propTypes.string]),
    accuracy: propTypes.oneOfType([propTypes.number, propTypes.string]),
  }),
  debounce: propTypes.bool,
  preventClear: propTypes.bool,
  defaultValue: propTypes.oneOfType([propTypes.number, propTypes.string]),
  disabled: propTypes.bool,
  labelWidth: propTypes.number,
  showLabel: propTypes.bool,
  fullWidth: propTypes.bool,
  width: propTypes.number,
  placeholder: propTypes.string,
  onChange: propTypes.func.isRequired,
  value: propTypes.oneOfType([propTypes.number, propTypes.string]),
  readOnly: propTypes.bool,
  prepend: propTypes.string,
  append: propTypes.string,
  valueAlign: propTypes.string,
  showDivider: propTypes.bool,
}

NumberField.defaultProps = {
  formFieldClassName: '',
  fieldKey: '',
  fullWidth: false,
  width: 100,
  showLabel: true,
  valueAlign: 'right',
  placeholder: '',
}


const CurrencyFieldTemplate = (props) => {
  return (
    <NumberField {...props} options={{...props.options, append: props.currencyShortDesc}}/>
  )
}

CurrencyFieldTemplate.defaultProps = {
  options: {
    accuracy: 2,
    min: 0,
    step: 10,
  },
}

CurrencyFieldTemplate.propTypes = {
  options: propTypes.object,
  currencyShortDesc: propTypes.string,
}

const mapStateToProps = (state) => ({currencyShortDesc: state.auth.user.currencyShortDesc})
const CurrencyField = connect(mapStateToProps)(CurrencyFieldTemplate)


class MemoField extends Component {
  focus = () => {
    if (this.input) this.input.focus()
  }

  render() {
    return (
      <TextFormField
        rows={3}
        {...this.props}
        multiLine={true}
        ref={e => {
          this.input = e
        }}/>
    )
  }
}

MemoField.propTypes = {
  disabled: propTypes.bool,
  formFieldClassName: propTypes.string,
  label: propTypes.node,
  onChange: propTypes.func,
  placeholder: propTypes.string,
  readOnly: propTypes.bool,
  rows: propTypes.number,
  rowsMax: propTypes.number,
  showDivider: propTypes.bool,
  showLabel: propTypes.bool,
  value: propTypes.string,
  valueKey: propTypes.string,
}

MemoField.defaultProps = {
  disabled: false,
  formFieldClassName: '',
  readOnly: false,
  rows: 3,
}


class SeparatorField extends Component {
  render() {
    const separatorStyle = {
      width: '100%',
      minHeight: '16px',
      backgroundColor: themeVars.palette.borderColor,
    }

    return <div style={separatorStyle}></div>
  }
}

SeparatorField.propTypes = {}
SeparatorField.defaultProps = {}


class DateField extends Component {

  getValueFromProps = ({value}) => value ? new Date(value) : null

  state = {value: this.getValueFromProps(this.props)}

  componentDidUpdate = (prevProps) => {
    if (!isEqual(prevProps.value, this.props.value))
      this.setState({value: this.getValueFromProps(this.props)})
  }

  onChange = (e, value) => {
    this.setState({value})
    this.props.onChange(value ? value.getTime() : null)
  }

  render() {
    const {
      allowNone,
      disabled,
      fieldKey,
      formFieldClassName,
      hint,
      label,
      labelWidth,
      mandatory,
      placeholder,
      readOnly,
      validateState,
    } = this.props
    const {value} = this.state

    const errorText = (value === '' && mandatory) ? 'This field is mandatory' : ''
    if (readOnly) {
      return <LabelField
        label={label}
        value={<DateComponent date={value}/>}/>
    }

    return (
      <FormField
        fieldKey={fieldKey}
        className={formFieldClassName || fieldKey}
        label={mandatory ? <>{label} *</> : label}
        labelWidth={labelWidth}
        disabled={disabled}
        hint={hint}
        mandatory={mandatory}
        validateState={validateState}
        value={value}>
        <DatePicker
          fullWidth={true}
          underlineShow={false}
          allowNone={allowNone}
          onChange={this.onChange}
          value={value}
          disabled={disabled}
          {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
        />
      </FormField>
    )
  }
}

DateField.propTypes = {
  fieldKey: propTypes.string,
  label: propTypes.node.isRequired,
  labelWidth: propTypes.number,
  hint: propTypes.string,
  value: propTypes.oneOfType([
    propTypes.number,
    propTypes.string,
  ]),
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  allowNone: propTypes.bool,
  placeholder: propTypes.string,
  mandatory: propTypes.bool,
  formFieldClassName: propTypes.string,
  onChange: propTypes.func,
  validateState: propTypes.bool,
}

DateField.defaultProps = {
  readOnly: false,
  disabled: false,
  allowNone: false,
  placeholder: '',
}


class TimeField extends Component {
  state = {
    value: this.props.value ? new Date(this.props.value) : null,
  }

  onChange = (e, value) => {
    this.setState({value})
    this.props.onChange(value ? value.getTime() : null)
  }

  render() {
    const {
      fieldKey,
      mandatory,
      readOnly,
      disabled,
      placeholder,
      hint,
      validateState,
    } = this.props
    const {label} = this.props
    const {value} = this.state

    const errorText = (value === '' && mandatory) ? 'This field is mandatory' : ''
    if (readOnly) {
      return <LabelField
        label={label}
        value={DateHelper.getTimeString(value)}/>
    }

    return (
      <FormField
        fieldKey={fieldKey}
        className={fieldKey}
        label={mandatory ? <>{label} *</> : label}
        disabled={disabled}
        hint={hint}
        mandatory={mandatory}
        validateState={validateState}
        value={value}>
        <TimePicker
          fullWidth={true}
          underlineShow={false}
          onChange={this.onChange}
          value={value}
          disabled={disabled}
          {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
        />
      </FormField>
    )
  }
}

TimeField.propTypes = {
  fieldKey: propTypes.string,
  label: propTypes.node.isRequired,
  hint: propTypes.string,
  value: propTypes.oneOfType([
    propTypes.number,
    propTypes.string,
  ]),
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  placeholder: propTypes.string,
  mandatory: propTypes.bool,
  onChange: propTypes.func,
  validateState: propTypes.bool,
}

TimeField.defaultProps = {
  readOnly: false,
  disabled: false,
  placeholder: '',
}

class DateTimeField extends Component {
  state = {
    value: this.props.value ? new Date(this.props.value) : null,
    selectorVisible: false,
  }

  showSelector = () => {
    this.setState({selectorVisible: true})
  }

  hideSelector = () => {
    this.setState({selectorVisible: false})
  }

  onDateChange = (e, value) => {
    const {defaultToMidnight, onChange} = this.props

    this.setState({selectorVisible: false})
    const newValue = value
      ? defaultToMidnight
        ? setTimeToMidnight(value.getTime())
        : value.getTime()
      : null

    onChange(newValue, 'date')
  }

  onTimeChange = (e) => {
    const value = e.target.value
    const {value: dateValue, onChange} = this.props
    if (isEmpty(value)) {
      const updatedDate = moment(dateValue).set({minute: 0, hour: 0})
      onChange(updatedDate, 'time')
      return
    }

    const time = moment(value, 'HH:mm')
    const hour = moment(time).get('hour')
    const minutes = moment(time).get('minute')

    const date = moment(dateValue)
      .set({minute: 0, hour: 0})
      .add(hour, 'h')
      .add(minutes, 'm')

    onChange(date.valueOf(), 'time')
  }

  getDatePicker = () => {
    const {
      allowNone,
      disabled,
      disableDatePart,
      errorText,
      hideDate,
      mandatory,
      placeholder,
      showToggle,
      toggled,
      validateState,
      value,
    } = this.props

    return showDateTimeFieldDateValue(hideDate, showToggle, toggled)
      ? (
        <div className={getClassName('dateTime-datePicker')}>
          <DatePicker
            allowNone={allowNone}
            disabled={disabled || disableDatePart}
            fullWidth={true}
            onChange={this.onDateChange}
            onClick={(e) => this.showSelector(e)}
            onClose={this.hideSelector}
            onDismiss={(e) => this.hideSelector(e)}
            underlineShow={false}
            value={value ? new Date(value) : null}
            {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
          />
        </div>
      ) : null
  }

  getTimePicker = () => {
    const {allowNone, formFieldClassName, onBlur, showToggle, toggled, value} = this.props

    return showDateTimeFieldTimeValue(allowNone, showToggle, toggled, value)
      ? (
        <div className={getClassName('dateTime-timePicker')}>
          <TextField
            id={`${formFieldClassName}-time`}
            onBlur={onBlur}
            onChange={this.onTimeChange}
            type={'time'}
            InputProps={{disableUnderline: true}}
            value={moment(value).format('HH:mm')}
          />
        </div>
      )
      : null
  }

  render() {
    const {
      disabled,
      fieldKey,
      formFieldClassName,
      hint,
      label,
      labelWidth,
      mandatory,
      onToggleChange,
      readOnly,
      showToggle,
      toggled,
      value,
      regionalDateFormat,
      locale,
    } = this.props
    const {selectorVisible} = this.state

    if (readOnly) {
      return <LabelField
        label={label}
        labelWidth={labelWidth}
        value={DateHelper.getDateTimeString(value, regionalDateFormat, locale)}
      />
    }

    return (
      <FormField
        active={selectorVisible}
        className={formFieldClassName || fieldKey}
        disabled={disabled}
        fieldKey={fieldKey}
        hint={hint}
        label={mandatory ? <>{label} *</> : label}
        labelWidth={labelWidth}
        onToggleChange={onToggleChange}
        showToggle={showToggle}
        toggled={toggled}
      >
        <div className={getClassName('dateTime-container')}>
          {this.getDatePicker()}
          {this.getTimePicker()}
        </div>
      </FormField>
    )
  }
}

DateTimeField.propTypes = {
  allowNone: propTypes.bool,
  defaultToMidnight: propTypes.bool,
  disabled: propTypes.bool,
  disableDatePart: propTypes.bool,
  errorText: propTypes.string,
  fieldKey: propTypes.string,
  formFieldClassName: propTypes.string,
  hideDate: propTypes.bool,
  hint: propTypes.string,
  label: propTypes.node.isRequired,
  labelWidth: propTypes.number,
  mandatory: propTypes.bool,
  onBlur: propTypes.func,
  onChange: propTypes.func,
  onToggleChange: propTypes.func,
  placeholder: propTypes.string,
  readOnly: propTypes.bool,
  showToggle: propTypes.bool,
  toggled: propTypes.bool,
  validateState: propTypes.bool,
  value: propTypes.oneOfType([
    propTypes.number,
    propTypes.string,
  ]),
  regionalDateFormat: propTypes.string,
  locale: propTypes.string,
}

DateTimeField.defaultProps = {
  allowNone: false,
  defaultToMidnight: false,
  disabled: false,
  disableDatePart: false,
  formFieldClassName: '',
  placeholder: '',
  readOnly: false,
  showToggle: false,
}


class DateRangeField extends Component {

  onChange = (start, end, timeKey) => {
    const {onChange} = this.props
    onChange({start, end, timeKey})
  }

  render() {
    const {
      fieldKey,
      mandatory,
      readOnly,
      disabled,
      placeholder,
      hint,
      formFieldClassName,
      validateState,
      presetTypes,
      labelPrefix,
      value,
    } = this.props

    const {label} = this.props
    if (readOnly) return <LabelField label={label} value={<DateComponent date={value}/>}/>

    const {start, end, timeKey} = value || {}

    return (
      <FormField
        fieldKey={fieldKey}
        className={formFieldClassName || fieldKey}
        label={mandatory ? <>{label} *</> : label}
        disabled={disabled}
        hint={hint}
        mandatory={mandatory}
        validateState={validateState}
        value={value}>

        <DatePickerRangeSelectField
          startDate={start}
          endDate={end}
          timeKey={timeKey}
          placeholder={placeholder}
          labelPrefix={labelPrefix}
          showPresets={true}
          presetTypes={presetTypes}
          onChange={this.onChange}
          {...getSelectFieldProps()}
        />
      </FormField>
    )
  }
}

DateRangeField.propTypes = {
  fieldKey: propTypes.string,
  label: propTypes.node.isRequired,
  hint: propTypes.string,
  value: propTypes.shape({
    start: propTypes.number,
    end: propTypes.number,
    timeKey: propTypes.string,
  }),
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  allowNone: propTypes.bool,
  placeholder: propTypes.string,
  presetTypes: propTypes.string,
  labelPrefix: propTypes.string,
  mandatory: propTypes.bool,
  formFieldClassName: propTypes.string,
  onChange: propTypes.func,
  validateState: propTypes.bool,
}

DateRangeField.defaultProps = {
  readOnly: false,
  disabled: false,
  allowNone: false,
  placeholder: '',
  presetTypes: 'future-short',
}

class CampaignSelectorField extends Component {
  state = {
    value: this.props.value ? this.props.value : null,
  }

  onChange = (value) => {
    this.setState({value})
    this.props.onChange(value)
  }

  render() {
    const {fieldKey, label, mandatory, readOnly, disabled} = this.props
    const {value} = this.state

    return (
      <FormField
        fieldKey={fieldKey}
        className={fieldKey}
        label={mandatory ? <>{label} *</> : label}
        disabled={disabled}
      >
        <CampaignSelector
          value={value}
          onChange={this.onChange}
          disabled={readOnly || disabled}
        />
      </FormField>
    )
  }
}

CampaignSelectorField.propTypes = {
  fieldKey: propTypes.string,
  label: propTypes.node.isRequired,
  value: propTypes.oneOfType([
    propTypes.number,
    propTypes.string,
  ]),
  readOnly: propTypes.bool,
  disabled: propTypes.bool,
  mandatory: propTypes.bool,
  onChange: propTypes.func,
}
CampaignSelectorField.defaultProps = {
  readOnly: false,
  disabled: false,
}

const LabelField = (props) => {
  const {className, showLabel} = props
  const color = (!props.color || props.color === 'default') ? themeVars.palette.primaryFontColor : props.color
  let width = (props.width) ? props.width : '100%'
  width = (props.fullWidth) ? width : ''

  const valueWidth = props.valueWidth || 'auto'
  const valueAlign = props.valueAlign || 'left'
  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      minHeight: '48px',
      width: width,
      borderBottom: `1px solid ${themeVars.palette.borderColor}`,
    },
    title: {
      marginLeft: 24,
      width: props.labelWidth || 150,
      flexGrow: 0,
      flexShrink: 0,
      color: themeVars.palette.disabledColor,
      fontSize: '16px',
      overflow: 'hidden',
    },
    value: {
      marginRight: 20,
      color: color,
      width: valueWidth,
      fontSize: '16px',
      textAlign: valueAlign,
      flexGrow: (valueWidth === 'auto') ? 1 : 0,
    },
    placeholder: {
      marginRight: 20,
      color: themeVars.palette.disabledColor,
      width: valueWidth,
      fontSize: '16px',
      textAlign: valueAlign,
      flexGrow: (valueWidth === 'auto') ? 1 : 0,
    },
    prepend: {
      marginLeft: 24,
      fontSize: 16,
    },
    append: {
      marginRight: 20,
      fontSize: 16,
    },
  }

  let prepend, append, valueEl
  if (!isNil(props.value)) {
    valueEl = <div className="labelField-value" style={styles.value}>
      {props.value}
    </div>
    if (props.prepend) prepend = <div className="labelField-prepend" style={styles.prepend}>
      {props.prepend}
    </div>
    if (props.append) append = <div className="labelField-append" style={styles.append}>
      {props.append}
    </div>

  } else if (props.placeholder) {
    valueEl = <div className="labelField-placeholder" style={styles.placeholder}>
      {props.placeholder}
    </div>
  }

  return (
    <div className={`labelField ${className}`} style={styles.container}>
      {showLabel &&
        <div className="labelField-title" style={styles.title}>
          {props.label}
        </div>}
      {prepend}
      {valueEl}
      {append}
    </div>
  )
}

LabelField.propTypes = {
  className: propTypes.string,
  label: propTypes.node,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.object,
  ]),
  width: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
  ]),
  valueWidth: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
  ]),
  labelWidth: propTypes.number,
  fullWidth: propTypes.bool,
  valueAlign: propTypes.string,
  color: propTypes.string,
  prepend: propTypes.string,
  append: propTypes.any,
  placeholder: propTypes.string,
  showLabel: propTypes.bool,
}

LabelField.defaultProps = {
  className: '',
  showLabel: true,
}

const WarningLabelField = (props) => {
  const color = (!props.color || props.color === 'default')
    ? themeVars.palette.disabledColor
    : props.color
  const valueAlign = props.valueAlign || 'left'
  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      minHeight: 48,
      borderBottom: `1px solid ${themeVars.palette.borderColor}`,
      paddingLeft: 24,
    },
    label: {
      width: 150,
      flexGrow: 0,
      flexShrink: 0,
      color: color,
      fontSize: 16,
      overflow: 'hidden',
      textAlign: valueAlign,
    },
    icon: {
      marginRight: 8,
    },
  }
  const icon = props.icon
    ? <Icon style={styles.icon} name={props.icon} color={color}/>
    : null
  return (
    <div className="warningLabelField-container" style={styles.container}>
      {icon}
      <div className="warningLabelField-label" style={styles.label}>
        {props.label}
      </div>
    </div>
  )
}

WarningLabelField.propTypes = {
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
  ]).isRequired,
  color: propTypes.string,
  icon: propTypes.string,
  valueAlign: propTypes.string,
}

const ImageField = (props) => {
  const {label, value} = props
  const imageStyles = {
    width: (props.imageWidth) ? props.imageWidth : 230,
    height: (props.imageHeight) ? props.imageHeight : 100,
    margin: '12px 0px',
    border: (isNil(value)) ? '' : `1px solid ${themeVars.palette.borderColor}`,
  }
  const image = <img style={imageStyles} src={value} alt={label}/>
  return (
    <LabelField label={label} value={image}/>
  )
}

ImageField.propTypes = {
  label: propTypes.node.isRequired,
  value: propTypes.string,
  imageWidth: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
  ]),
  imageHeight: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
  ]),
}

class ContactDetailsField extends Component {
  render() {
    const {fieldKey, contact} = this.props
    const phone = get(contact, 'phone', null)
    const mobile = get(contact, 'mobile', null)
    const email = get(contact, 'email', null)

    return (
      <FormField fieldKey={fieldKey} className={fieldKey} showLabel={false}>
        <div className={getClassName('contact-details-field')}>
          {phone && <Phone number={phone}/>}
          {mobile && <Phone number={mobile} isMobile/>}
          {email && <Email email={email}/>}
        </div>
      </FormField>
    )
  }
}

ContactDetailsField.propTypes = {
  fieldKey: propTypes.string,
  contact: propTypes.shape({
    phone: propTypes.string,
    mobile: propTypes.string,
    email: propTypes.string,
  }),
}

class DeliveryNoteListField extends ListField {
  getListField = (value, disabled, errorText, validateState) => {
    const {
      allowNew,
      autoFocus,
      deliveryNoteId,
      fields,
      excludeLinkedToDelivery,
      excludeReturnNotes,
      mandatory,
      multi,
      orderId,
      placeholder,
      status,
      values,
    } = this.props

    value = !isNil(deliveryNoteId) ? deliveryNoteId : value

    return (
      <DeliveryNoteSelectField
        ref={e => {
          this.focus = e
        }}
        orderId={orderId}
        clearable={!mandatory}
        onChange={this.onChange}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        value={value || values}
        allowNew={allowNew}
        disabled={disabled}
        displayInvoicedDate={true}
        fields={fields}
        excludeLinkedToDelivery={excludeLinkedToDelivery}
        excludeReturnNotes={excludeReturnNotes}
        status={status}
        autoFocus={autoFocus}
        multi={multi}
        {...getRequiredFieldProps(validateState, mandatory, placeholder, errorText, value)}
        {...getSelectFieldProps()}
      />
    )
  }
}

export default FormField

export {
  StringField,
  NumberField,
  BooleanField,
  ListField,
  AsyncListField,
  DateField,
  TimeField,
  DateTimeField,
  DateRangeField,
  MultiListField,
  MemoField,
  PhoneField,
  EmailField,
  CurrencyField,
  CompanyListField,
  PackTypeListField,
  PartCategoryListField,
  PartListField,
  ContactListField,
  CampaignSelectorField,
  OpportunityListField,
  ReferenceListField,
  OrderListField,
  OrderSelectorField,
  PurchaseOrderListField,
  LabelField,
  WarningLabelField,
  ImageField,
  TextFormField,
  DurationPicker,
  RepeatDaysField,
  RepeatEndPeriodField,
  TimeSpentField,
  RepeatMonthsField,
  ContactDetailsField,
  DeliveryNoteListField,
  CheckBoxTextField,
  CheckBoxField,
  SeparatorField,
}
